/**
 * 작업(batch) 목 데이터.
 * 출처: 와이어프레임 `STATES`(959행) · 작업 목록 `B`(2193행) · `HIST_ROWS`(1901행) · `JOBS`(1456행).
 *
 * 🔴 **`allowed_commands` 는 서버가 준 값처럼 «데이터»로 담는다.**
 *    와이어프레임의 `S()`(985행)는 `phase` 로 배열을 계산해 만드는데, 그건 데모라서 그런 것이고
 *    우리 규약 위반이다(README §11-3 #3). 여기에는 계산 함수를 두지 않는다.
 */
import type { Batch, BatchListRow } from '@ctcells/contract'

import { RECIPE_ICC_CK, RECIPE_ICC_STANDARD, RECIPE_ICC_V0 } from './recipes.ts'

/**
 * `Batch.reagent_basis`(정본 v0.9.1 신설) — 서버가 **그 배치가 고정한 레시피 판**의 `reagents` 에서 편다
 * (`ctcells-core app/views/batches.py` `reagent_basis_of`). 목록·단건 모두 **언제나 배열**이다(시약 정의가 없으면 `[]`).
 * 서버 규칙 그대로: 채널 번호 차례 · `reagent` = 시약 `name`(비면 채널 키) · `labware_id` 없으면 `null` ·
 * `volume_by_well` = `volumeByWell` 의 수 값만 · `basis` 는 늘 `recipe_assumed`(간주 값이지 잰 값이 아니다).
 */
function reagentBasisOf(recipe: typeof RECIPE_ICC_STANDARD): Batch['reagent_basis'] {
  const table = recipe.payload.reagents ?? {}
  return Object.keys(table)
    .sort((a, b) => Number(a) - Number(b))
    .flatMap((key) => {
      const def = table[key]
      if (def === undefined) return []
      return [
        {
          reagent: def.name === '' ? key : def.name,
          labwareId: def.labware_id ?? null,
          volumeByWell: { ...(def.volumeByWell ?? {}) },
          basis: 'recipe_assumed' as const,
        },
      ]
    })
}

const base = {
  recipe_id: RECIPE_ICC_STANDARD.recipe_id,
  recipe_version: 1,
  completion_basis: null,
  recovery_verdict: null,
  review_reason: null,
  /*
   * 🔴 아래 다섯 칸은 서버가 목록·단건 모두 **언제나 싣는다**(`ctcells-core app/views/batches.py`
   *    `_dto` · 2026-09-26 실측 50/50). 배정이 없으면 `sample_assignments` 는 `[]` 다 — null·부재가 아니다
   *    (그 칸은 `as const` 가 읽기 전용 튜플로 굳혀 `Batch` 에 안 맞으므로 객체마다 적는다).
   *    `name` 은 만들 때 이름(저장값), `recipe_name` 은 **지금** 레시피 이름이다(두 시계).
   * 🔴 **값 모양은 실물 그대로다**(2026-09-26 실측 원문 `"name": "fe-20260922-full-r1 · 조형우 ·
   *    2026-09-23 12:46"` · `"created_by": "8e2403f9-…"`).
   *    · `name` = 「<레시피 이름> · <수행인 이름> · <생성 시각 KST YYYY-MM-DD HH:MM>」 — 서버가 조립한다.
   *      화면이 조립하지 않으므로 fixture 도 **완성된 문자열**로 둔다
   *    · `created_by` = **계정 ID**(`batch.user_id`) — 이름이 아니다. fixture 계정 표(`users.ts`)의 id 꼴을 쓴다
   *    · `sample_count` = 그 배치에 붙은 시료 수(서버가 센다)
   */
  name: `${RECIPE_ICC_STANDARD.name} · 조형우 · 2026-09-11 14:10`,
  created_by: 'usr-hyungwoo',
  sample_count: 9,
  recipe_name: RECIPE_ICC_STANDARD.name,
  reagent_basis: reagentBasisOf(RECIPE_ICC_STANDARD),
  /*
   * v0.9.4 네 칸(2026-10-11 재생성 · 서버 `app/views/batches.py` `_dto` 가 멈춰 있지 않으면 `null` 로 «실어서» 낸다).
   * `halt_reason` 멈춘 까닭 한 줄 · `halt_resume` auto|human · `resume_blockers` 지금 [재개]를 막는 문장 목록(안 멈췄으면 null · 빈 목록 =
   * 재개만 누르면 된다) · `reagent_module_id` 시약 보충 확인을 보낼 염색 모듈(활성 염색 모듈이 정확히 하나일 때).
   * 멈춘 fixture(`BATCH_PAUSED` · `BATCH_ERROR`)는 아래서 서버 문면(`QUARANTINE_BLOCKER` · `RECOVERY_BLOCKER`)으로 채운다.
   */
  halt_reason: null,
  halt_resume: null,
  resume_blockers: null,
  resume_headline: null,
  reagent_module_id: 'staining-1',
  /*
   * v0.9.4 계약 0.28.15 칸(2026-10-22 재생성 · 서버 `app/views/batches.py` `_dto`) — 안 멈춘 배치는 실물 50/50 이 이 값이다
   * (2026-10-22 로컬 실측 `GET /batches?limit=50`). 배열 둘은 `[]` 다 — null·부재가 아니다.
   * ⚠ 배열은 `as const` 가 읽기 전용 튜플로 굳히지 않게 타입을 붙인다(`Batch` 의 배열 칸은 가변 배열이다).
   */
  halt_code: null,
  /* v0.9.5 서버 halt_group_for: 멈춤 밖 · 코드 미상이면 null(문장에서 원인을 추론하지 않는다). */
  halt_group: null,
  halt_module_id: null,
  halt_remote_at: null,
  halt_delay: null,
  resume_route: null,
  resume_checks: [] as Batch['resume_checks'],
  resume_timed_atom_choices: [] as Batch['resume_timed_atom_choices'],
} as const

/** 실행 중 — 일시정지·중지를 받는다 */
export const BATCH_RUNNING: Batch = {
  ...base,
  /*
   * 🔴 배정 9건 — `samples.ts` 의 이 작업 시료(`assigned_well`)와 **같은 짝**이다. 차례는 서버가 세우는
   *    대로 `assigned_well` 사전순이다(`services/batches.py` `assignments_for` 의 `order_by`).
   * ⚠ **8건이고 `sample_count` 는 9다** — `S-1043` 은 이 작업에 붙었지만 자리가 없다(`samples.ts`).
   *   계약 원문 「`sample_assignments` 길이와 다를 수 있다」의 실물 모양이다. 맞추지 마라.
   */
  sample_assignments: [
    { wellName: 'A1', sample_id: 'S-1041' },
    { wellName: 'A2', sample_id: 'S-1042' },
    { wellName: 'A3', sample_id: 'S-1045' },
    { wellName: 'B2', sample_id: 'S-1044' },
    { wellName: 'B3', sample_id: 'S-1046' },
    { wellName: 'C1', sample_id: 'S-1047' },
    { wellName: 'C2', sample_id: 'S-1048' },
    { wellName: 'C3', sample_id: 'Ref' },
  ],
  batch_id: 'B-260911-02',
  created_at: '2026-09-11T14:10:00+09:00',
  started_at: '2026-09-11T13:35:03+09:00',
  completed_at: null,
  status: 'running',
  version: 12,
  allowed_commands: ['pause', 'stop'],
}

/** 적재 대기 — 사람이 트레이를 넣을 때까지 진행이 없다. 명령 집합은 실행 중과 같다 */
export const BATCH_READY: Batch = {
  ...BATCH_RUNNING,
  started_at: '2026-09-11T13:56:53+09:00',
  version: 8,
  allowed_commands: ['pause', 'stop'],
}

/** 일시정지 — 재개·중지 */
export const BATCH_PAUSED: Batch = {
  ...BATCH_RUNNING,
  status: 'paused',
  version: 14,
  allowed_commands: ['resume', 'stop'],
  /* 사람이 멈춘 배치 — 막는 것 없음(빈 목록 = 재개만 누르면 된다 · 서버 `resume_blockers_for` 1.44) */
  halt_reason: '사용자 일시정지',
  /* 사유 행이 없는 사람 일시정지(paused)는 서버가 operator로 분류한다. */
  halt_group: 'operator',
  halt_resume: 'human',
  resume_blockers: [],
  resume_headline: { kind: 'ready', text: '오류가 풀렸습니다 — [재개]를 누르면 이어서 진행합니다' },
  /* 사람이 멈춘 `paused` — 확인 재개가 필요 없으면 [재개] 길이다(서버 `resume_route_for` · 확인 칸 없음) */
  resume_route: 'resume',
}

/** 안전정지로 멈춘 뒤 복구 판정을 기다린다. 🔴 재개는 «허용 집합에 없다» — 서버가 안 준다 */
export const BATCH_ERROR: Batch = {
  ...base,
  sample_assignments: [],
  batch_id: 'B-260911-02',
  created_at: '2026-09-11T14:10:00+09:00',
  started_at: '2026-09-11T14:05:58+09:00',
  completed_at: null,
  status: 'error',
  version: 19,
  recovery_verdict: 'inconclusive',
  review_reason: 'recovery_decision',
  allowed_commands: ['stop', 'recover'],
  /* v0.9.5 서버 services/batches.py _blocker_line · QUARANTINE_BLOCKER · RECOVERY_BLOCKER 그대로(2026-10-28). */
  halt_reason: '안전정지 — 이송 중 비상정지',
  halt_resume: 'human',
  resume_blockers: [
    {
      code: 'handoff_quarantine',
      text: '이송 자리가 격리돼 있습니다 — [해제]에서 물리 확인 뒤 푸십시오',
      exit: { kind: 'physical', todo: null },
    },
    {
      code: 'recovery_required',
      text: '[재개]로는 이어 갈 수 없습니다 — [복구 판정]에서 폐기 · 완료 선언 · 선택 시료 폐기 가운데 하나를 고르십시오',
      exit: { kind: 'button', todo: null },
    },
  ],
  resume_headline: { kind: 'act', text: '조치가 필요합니다' },
  /* `error` 는 확인 재개 판정 밖 — 서버가 `resume` 을 낸다(`resume_route_for` · `paused` 에서만 `confirm` 을 가린다) */
  resume_route: 'resume',
}

/** 만들어만 둔 작업 — 시작만 받는다 */
export const BATCH_CREATED: Batch = {
  ...base,
  name: `${RECIPE_ICC_STANDARD.name} · 조형우 · 2026-09-11 12:40`,
  sample_count: 4,
  sample_assignments: [],
  batch_id: 'B-260911-03',
  created_at: '2026-09-11T12:40:00+09:00',
  started_at: null,
  completed_at: null,
  status: 'created',
  version: 1,
  allowed_commands: ['start'],
}

/** 종착(완료). 상태변경 명령이 «하나도» 없다 — 빈 배열도 서버가 준 값이다 */
export const BATCH_COMPLETED: Batch = {
  ...base,
  name: `${RECIPE_ICC_STANDARD.name} · eric.cho · 2026-09-11 02:50`,
  created_by: 'usr-eric',
  sample_assignments: [],
  batch_id: 'B-260911-01',
  created_at: '2026-09-11T02:50:00+09:00',
  started_at: '2026-09-11T03:02:00+09:00',
  completed_at: '2026-09-11T11:58:00+09:00',
  status: 'completed',
  completion_basis: 'auto',
  version: 41,
  allowed_commands: [],
}

/** 완료를 «관측하지 못한» 배치 — 사람이 종착을 선언해야 한다(`confirm_completed`) */
export const BATCH_COMPLETION_UNOBSERVED: Batch = {
  ...base,
  sample_assignments: [],
  batch_id: 'B-260911-07',
  recipe_id: RECIPE_ICC_CK.recipe_id,
  recipe_name: RECIPE_ICC_CK.name,
  recipe_version: 3,
  name: `${RECIPE_ICC_CK.name} · 조형우 · 2026-09-11 05:00`,
  created_at: '2026-09-11T05:00:00+09:00',
  started_at: '2026-09-11T05:12:00+09:00',
  completed_at: null,
  status: 'running',
  version: 37,
  review_reason: 'completion_unobserved',
  allowed_commands: ['recover'],
}

export const BATCHES: Batch[] = [
  BATCH_RUNNING,
  BATCH_CREATED,
  BATCH_COMPLETED,
  BATCH_COMPLETION_UNOBSERVED,
]

/**
 * 종착 상태 둘. 🔴 **서버 어휘 그대로다** — `app/services/batches.py` `TERMINAL_STATUSES`.
 * ⚠ 나머지 다섯(`created`·`queued`·`running`·`paused`·`error`)은 아직 끝나지 않은 것이다.
 */
const TERMINAL: ReadonlySet<Batch['status']> = new Set(['completed', 'aborted'])

/**
 * 🔴 **`GET /batches?phase=active` 가 주는 그대로** — demo 가 그 응답 자리에 서는 값이다.
 *
 * 두 가지를 서버와 맞춘다. 맞추지 않으면 **목에서만 도는 화면**이 생긴다.
 * 1. **고르기는 여집합**이다(`status NOT IN (completed, aborted)`). 도는 것을 손으로 열거하면
 *    새 상태 어휘가 어느 칸에도 안 들어가 조용히 사라진다 — 서버가 여집합을 쓰는 까닭이 그것이다.
 * 2. **차례는 `created_at desc`** 다(`_order(phase='active')`). 상시 작업 줄이 「한 건」을 고를 때
 *    (`pickStripBatch`) 서버가 세워 준 차례에 기대므로, 목이 다른 차례를 주면 **목과 실물이
 *    다른 작업을 띄운다.**
 */
export const ACTIVE_BATCHES: Batch[] = BATCHES.filter((batch) => !TERMINAL.has(batch.status)).sort(
  (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
)

/**
 * 작업 목록 표(화면 전용 합성 — `BatchListRow` 주석 참조).
 * 목록에는 **종착하지 않은 작업만** 나온다. `completed`·`aborted` 는 이력 화면으로 넘어간다.
 *
 * ⚠ **진행(`progress_text`) 칸은 없다**(2026-09-30) — 목록의 「진행」 열이 빠져 행 타입에서도 걷었다(`BatchListRow` 주석).
 * 🔴 `created_at` · `started_at` · `completed_at` 은 **정렬용 원값**이다(표기 `*_text` 와 같은 시각 · 화면에 안 그린다).
 */
export const BATCH_LIST: BatchListRow[] = [
  {
    batch_id: 'B-260911-02',
    recipe_name: RECIPE_ICC_STANDARD.name,
    recipe_version: 2,
    created_by: 'usr-hyungwoo',
    sample_count: 9,
    status: 'running',
    halt_reason: null,
    created_text: '09-11 14:15',
    started_text: '14:18',
    completed_text: '—',
    duration_text: '—',
    created_at: '2026-09-11T14:15:00+09:00',
    started_at: '2026-09-11T14:18:00+09:00',
    completed_at: null,
  },
  {
    batch_id: 'B-260911-05',
    recipe_name: RECIPE_ICC_CK.name,
    recipe_version: 1,
    created_by: 'usr-hyungwoo',
    sample_count: 9,
    status: 'paused',
    halt_reason: null,
    created_text: '09-11 12:58',
    started_text: '13:02',
    completed_text: '—',
    duration_text: '—',
    created_at: '2026-09-11T12:58:00+09:00',
    started_at: '2026-09-11T13:02:00+09:00',
    completed_at: null,
  },
  {
    batch_id: 'B-260911-03',
    recipe_name: RECIPE_ICC_STANDARD.name,
    recipe_version: 2,
    created_by: 'usr-eric',
    sample_count: 4,
    status: 'created',
    halt_reason: null,
    created_text: '09-11 14:02',
    started_text: '—',
    completed_text: '—',
    duration_text: '—',
    created_at: '2026-09-11T14:02:00+09:00',
    started_at: null,
    completed_at: null,
  },
  {
    batch_id: 'B-260911-04',
    recipe_name: RECIPE_ICC_CK.name,
    recipe_version: 1,
    created_by: 'usr-hyungwoo',
    sample_count: 9,
    status: 'created',
    halt_reason: null,
    created_text: '09-11 14:05',
    started_text: '—',
    completed_text: '—',
    duration_text: '—',
    created_at: '2026-09-11T14:05:00+09:00',
    started_at: null,
    completed_at: null,
  },
  {
    batch_id: 'B-260910-01',
    recipe_name: RECIPE_ICC_V0.name,
    recipe_version: 1,
    created_by: 'usr-eric',
    sample_count: 6,
    status: 'error',
    halt_reason: '모듈 응답 없음 — 원심분리기 하트비트 단절',
    created_text: '09-10 00:08',
    started_text: '09-10 00:12',
    completed_text: '—',
    duration_text: '—',
    created_at: '2026-09-10T00:08:00+09:00',
    started_at: '2026-09-10T00:12:00+09:00',
    completed_at: null,
  },
  {
    batch_id: 'B-260909-02',
    recipe_name: RECIPE_ICC_V0.name,
    recipe_version: 1,
    created_by: 'usr-hyungwoo',
    sample_count: 9,
    status: 'error',
    halt_reason: '적재 확약 지연 — 작업자 조치 대기',
    created_text: '09-09 15:36',
    started_text: '09-09 15:40',
    completed_text: '—',
    duration_text: '—',
    created_at: '2026-09-09T15:36:00+09:00',
    started_at: '2026-09-09T15:40:00+09:00',
    completed_at: null,
  },
]

/** 작업 이력 — 종착한 작업만. 오래된 것부터 */
export const BATCH_HISTORY: BatchListRow[] = [
  {
    batch_id: 'B-260909-01',
    recipe_name: RECIPE_ICC_V0.name,
    recipe_version: 1,
    created_by: 'usr-eric',
    sample_count: 9,
    status: 'completed',
    halt_reason: null,
    created_text: '09-09 03:04',
    started_text: '09-09 03:10',
    completed_text: '09-09 12:20',
    duration_text: '09:10:00',
    created_at: '2026-09-09T03:04:00+09:00',
    started_at: '2026-09-09T03:10:00+09:00',
    completed_at: '2026-09-09T12:20:00+09:00',
  },
  {
    batch_id: 'B-260910-02',
    recipe_name: RECIPE_ICC_STANDARD.name,
    recipe_version: 2,
    created_by: 'usr-hyungwoo',
    sample_count: 9,
    status: 'aborted',
    halt_reason: null,
    created_text: '09-10 00:05',
    started_text: '09-10 00:12',
    completed_text: '09-10 00:58',
    duration_text: '00:46:00',
    created_at: '2026-09-10T00:05:00+09:00',
    started_at: '2026-09-10T00:12:00+09:00',
    completed_at: '2026-09-10T00:58:00+09:00',
  },
  {
    batch_id: 'B-260910-03',
    recipe_name: RECIPE_ICC_STANDARD.name,
    recipe_version: 2,
    created_by: null,
    sample_count: 5,
    status: 'completed',
    halt_reason: null,
    created_text: '09-10 02:35',
    started_text: '09-10 02:40',
    completed_text: '09-10 11:31',
    duration_text: '08:51:00',
    created_at: '2026-09-10T02:35:00+09:00',
    started_at: '2026-09-10T02:40:00+09:00',
    completed_at: '2026-09-10T11:31:00+09:00',
  },
  {
    batch_id: 'B-260911-01',
    recipe_name: RECIPE_ICC_STANDARD.name,
    recipe_version: 2,
    created_by: 'usr-hyungwoo',
    sample_count: 9,
    status: 'completed',
    halt_reason: null,
    created_text: '09-11 02:58',
    started_text: '09-11 03:02',
    completed_text: '09-11 11:58',
    duration_text: '08:56:00',
    created_at: '2026-09-11T02:58:00+09:00',
    started_at: '2026-09-11T03:02:00+09:00',
    completed_at: '2026-09-11T11:58:00+09:00',
  },
]

/** IDLE 작업 줄의 「작업 선택 ▾」 드롭다운에 뜨는 대기 작업 */
export const PENDING_BATCHES: BatchListRow[] = BATCH_LIST.filter((b) => b.status === 'created')
